import './RoleTimeline.scss';
import General from '../../utils/GeneralUtils';

interface EvidenceItem {
  date: string;
  sourceType?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

interface RoleTimelineProps {
  firstSeen?: string;
  lastSeen?: string;
  sightings?: number;
  evidenceItems?: EvidenceItem[];
  days?: number;
}

const DEFAULT_DAYS = 730; // 2 years

function formatTimeAgo(dateStr: string | undefined): string {
  if (!dateStr) return '';
  return General.daysAgo(dateStr) || '';
}

export function RoleTimeline({ firstSeen, lastSeen, sightings, evidenceItems, days = DEFAULT_DAYS }: RoleTimelineProps) {
  if (!firstSeen && !lastSeen) {
    return null;
  }

  const now = new Date();
  const timelineStart = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  const timelineEnd = now;
  const totalMs = timelineEnd.getTime() - timelineStart.getTime();

  const firstSeenDate = firstSeen ? new Date(firstSeen) : null;
  const lastSeenDate = lastSeen ? new Date(lastSeen) : null;

  // Check if first seen is before the timeline window
  const isFirstSeenBeforeWindow = firstSeenDate ? firstSeenDate.getTime() < timelineStart.getTime() : false;

  // Calculate positions as percentages
  const firstSeenPos = firstSeenDate
    ? Math.max(0, Math.min(100, ((firstSeenDate.getTime() - timelineStart.getTime()) / totalMs) * 100))
    : 0;
  const lastSeenPos = lastSeenDate
    ? Math.max(0, Math.min(100, ((lastSeenDate.getTime() - timelineStart.getTime()) / totalMs) * 100))
    : 100;

  // Get source type and name for first/last evidence
  const sortedEvidence = [...(evidenceItems || [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const firstEvidence = sortedEvidence[0];
  const lastEvidence = sortedEvidence[sortedEvidence.length - 1];

  // Calculate intermediate evidence positions with source types and names
  const rawIntermediateItems = (evidenceItems || [])
    .map((item) => {
      const date = new Date(item.date);
      const pos = Math.max(0, Math.min(100, ((date.getTime() - timelineStart.getTime()) / totalMs) * 100));
      return { pos, sourceType: item.sourceType, date: item.date, firstName: item.firstName, middleName: item.middleName, lastName: item.lastName };
    })
    // Drop only what the start and end dots already draw. An item near either end is a
    // duplicate of that dot when it shares its source type - and a distinct fact when it
    // does not. One contact was crawled and district-verified nine hours apart, which is
    // 0.05% of a two-year track, so a source-blind band here hid the FOIA behind the
    // crawler dot entirely.
    .filter((item) => {
      const atStart = item.pos <= firstSeenPos + 1;
      const atEnd = item.pos >= lastSeenPos - 1;
      if (atStart && item.sourceType === firstEvidence?.sourceType) return false;
      if (atEnd && item.sourceType === lastEvidence?.sourceType) return false;
      return true;
    });

  // Deduplicate items that are within 1% of each other and have the same source type
  const dedupedIntermediateItems = rawIntermediateItems.reduce<{ pos: number; sourceType?: string; date: string; firstName?: string; middleName?: string; lastName?: string }[]>((acc, item) => {
    const existingSameType = acc.find(
      (existing) => existing.sourceType === item.sourceType && Math.abs(existing.pos - item.pos) <= 1
    );
    if (!existingSameType) {
      acc.push(item);
    }
    return acc;
  }, []);

  // Keeping a dot is not the same as showing it: every dot is 10px, so one landing on the
  // start or end position sits exactly underneath that dot and only changes which of the
  // two is visible. Nudge it clear. On a two-year track MIN_DOT_GAP is about six days of
  // visual drift, and the tooltip still reports the true date.
  const MIN_DOT_GAP = 1.2;
  const intermediateItems = dedupedIntermediateItems.map((item) => {
    let pos = item.pos;
    if (Math.abs(pos - firstSeenPos) < MIN_DOT_GAP) {
      pos = firstSeenPos + MIN_DOT_GAP;
    } else if (Math.abs(pos - lastSeenPos) < MIN_DOT_GAP) {
      pos = lastSeenPos - MIN_DOT_GAP;
    }
    return { ...item, pos: Math.max(0, Math.min(100, pos)) };
  });

  const formatTooltipHtml = (dateStr: string | undefined, sourceType?: string, firstName?: string, middleName?: string, lastName?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const dateFormatted = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const isFoia = sourceType === 'FOIA';
    const sourceLabel = isFoia ? 'District verified' : 'Found on the web';
    const sourceColor = isFoia ? 'var(--amplify-colors-yellow-60)' : 'var(--amplify-colors-green-60)';
    const nameParts = [firstName, middleName, lastName].filter(Boolean);
    const fullName = nameParts.length > 0 ? nameParts.join(' ') : null;
    return `<div><div style="color: ${sourceColor};">${sourceLabel}</div>${fullName ? `<div>${fullName}</div>` : ''}<div style="color: #888; font-size: 11px;">${dateFormatted}</div></div>`;
  };

  const getSourceClass = (sourceType?: string) => {
    if (sourceType === 'FOIA') return 'role-timeline__dot--foia';
    return 'role-timeline__dot--crawler';
  };

  const timelineStartLabel = timelineStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const timelineMidpoint = new Date(timelineStart.getTime() + totalMs / 2);
  const timelineMidLabel = timelineMidpoint.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  const isSingleSighting = firstSeen === lastSeen || (!lastSeen && firstSeen) || (!firstSeen && lastSeen);

  return (
    <div className="role-timeline">
      {/* Row 1: First/Last seen labels */}
      <div className="role-timeline__row role-timeline__row--seen">
        <span className="role-timeline__seen role-timeline__seen--first">
          {isSingleSighting ? `Seen ${formatTimeAgo(firstSeen || lastSeen)}` : `First seen ${formatTimeAgo(firstSeen)}`}
        </span>
        {!isSingleSighting && (
          <span className="role-timeline__seen role-timeline__seen--last">
            Last seen {formatTimeAgo(lastSeen)}
          </span>
        )}
      </div>

      {/* Row 2: Track with dots */}
      <div className="role-timeline__row role-timeline__row--track">
        <div className="role-timeline__track">
          {isFirstSeenBeforeWindow ? (
            <div
              className={`role-timeline__arrow role-timeline__arrow--left ${firstEvidence?.sourceType === 'FOIA' ? 'role-timeline__arrow--foia' : 'role-timeline__arrow--crawler'}`}
              style={{ left: `${firstSeenPos}%` }}
              data-tooltip-id="main-tooltip"
              data-tooltip-html={formatTooltipHtml(firstSeen, firstEvidence?.sourceType, firstEvidence?.firstName, firstEvidence?.middleName, firstEvidence?.lastName)}
            />
          ) : (
            <div
              className={`role-timeline__dot role-timeline__dot--start ${getSourceClass(firstEvidence?.sourceType)}`}
              style={{ left: `${firstSeenPos}%` }}
              data-tooltip-id="main-tooltip"
              data-tooltip-html={formatTooltipHtml(firstSeen, firstEvidence?.sourceType, firstEvidence?.firstName, firstEvidence?.middleName, firstEvidence?.lastName)}
            />
          )}
          <div
            className="role-timeline__line role-timeline__line--before"
            style={{ left: '0%', width: `${firstSeenPos}%` }}
          />
          <div
            className="role-timeline__line role-timeline__line--after"
            style={{ left: `${firstSeenPos}%`, width: `${100 - firstSeenPos}%` }}
          />
          {intermediateItems.map((item, idx) => (
            <div
              key={idx}
              className={`role-timeline__dot role-timeline__dot--intermediate ${getSourceClass(item.sourceType)}`}
              style={{ left: `${item.pos}%` }}
              data-tooltip-id="main-tooltip"
              data-tooltip-html={formatTooltipHtml(item.date, item.sourceType, item.firstName, item.middleName, item.lastName)}
            />
          ))}
          <div
            className={`role-timeline__dot role-timeline__dot--end ${getSourceClass(lastEvidence?.sourceType)}`}
            style={{ left: `${lastSeenPos}%` }}
            data-tooltip-id="main-tooltip"
            data-tooltip-html={formatTooltipHtml(lastSeen, lastEvidence?.sourceType, lastEvidence?.firstName, lastEvidence?.middleName, lastEvidence?.lastName)}
          />
        </div>
      </div>

      {/* Row 3: Date labels */}
      <div className="role-timeline__row role-timeline__row--labels">
        <span className="role-timeline__label role-timeline__label--start">{timelineStartLabel}</span>
        <span className="role-timeline__label role-timeline__label--mid">{timelineMidLabel}</span>
        <span className="role-timeline__label role-timeline__label--end">Today</span>
      </div>
    </div>
  );
}
